import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";

const ROTA_PECA = "ISAGA_ApontamentoSerigrafia/getPeca";
const ROTA_APONTAR = "ISAGA_ApontamentoSerigrafia/apontar";
const ROTA_VALIDAR_MATRICULA = "IAcompanhamentoMontagem/validate-matricula";
const STORAGE_MATRICULA = "listaMontagem_matriculas_SAP";

export const ModalApontamentoSerigrafia = ({ show, onHide }) => {
  const [ordemInput, setOrdemInput] = useState("");
  const [dadosPeca, setDadosPeca] = useState(null);
  const [matricula, setMatricula] = useState("");
  const [matriculaSAP, setMatriculaSAP] = useState("");
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [validandoMatricula, setValidandoMatricula] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRef = useRef(null);
  const apontandoRef = useRef(false);

  const validarMatriculaSAP = useCallback(async (valorMatricula) => {
    const valor = valorMatricula.trim();
    if (!/^\d{6}$/.test(valor)) return null;
    try {
      const { data } = await blogFetch.get(`${ROTA_VALIDAR_MATRICULA}/${encodeURIComponent(valor)}`);
      if (!data?.exists) return null;
      return { nome: data.nome ?? "", matriculaSAP: data.matricula_SAP ?? "" };
    } catch {
      return null;
    }
  }, []);

  const resetForm = () => {
    setOrdemInput("");
    setDadosPeca(null);
  };

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  const handleMatriculaChange = (valor) => {
    setMatricula(valor.replace(/\D/g, "").slice(0, 6));
    setMatriculaSAP("");
    setNomeUsuario("");
  };

  const buscarPeca = async (ordem) => {
    try {
      const { data } = await blogFetch.get(ROTA_PECA, { params: { ordem } });
      if (!data) {
        toast.error("Peça não encontrada.");
        return null;
      }
      setDadosPeca(data);
      return data;
    } catch (error) {
      console.error("Erro ao buscar peça:", error);
      toast.error(error?.response?.data?.mensagem || "Peça não encontrada.");
      setDadosPeca(null);
      return null;
    }
  };

  const dispararApontamento = async (ordemRaw, dados) => {
    if (apontandoRef.current) return;
    const ordem = (ordemRaw || "").trim();
    if (!ordem) {
      toast.error("Insira a ordem da peça.");
      return;
    }
    if (!/^\d{6}$/.test(matricula) || !matriculaSAP) {
      toast.error("Registre uma matrícula SAP válida.");
      return;
    }

    apontandoRef.current = true;
    setIsSubmitting(true);
    try {
      const info = dados || dadosPeca || (await buscarPeca(ordem));
      if (!info) return;

      const numeroOrdem = info.ordem_Serial || ordem;
      const { data } = await blogFetch.post(ROTA_APONTAR, {
        numeroOrdem,
        matriculaSap: matriculaSAP,
      });

      if (data?.success) {
        toast.success(data.mensagem || `Apontamento realizado para a ordem ${numeroOrdem}`);
        resetForm();
        inputRef.current?.focus();
      } else {
        toast.error(data?.mensagem || "Não foi possível apontar.");
      }
    } catch (error) {
      console.error("Erro ao realizar apontamento:", error);
      const apiData = error?.response?.data;
      const msg = typeof apiData === "string" ? apiData : (apiData?.mensagem || apiData?.message);
      toast.error(msg || "Erro ao realizar apontamento.");
    } finally {
      setIsSubmitting(false);
      apontandoRef.current = false;
    }
  };

  const handleOrdemKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    dispararApontamento(ordemInput);
  };

  useEffect(() => {
    if (!show) return;
    const armazenado = localStorage.getItem(STORAGE_MATRICULA);
    if (!armazenado) return;

    let valor = "";
    let nomeSalvo = "";
    let matriculaSAPSalva = "";
    try {
      const obj = JSON.parse(armazenado);
      valor = obj.principal || obj.secundaria || "";
      nomeSalvo = obj.nome || "";
      matriculaSAPSalva = obj.matriculaSAP || "";
    } catch {
      valor = armazenado;
    }
    if (!valor) return;

    setMatricula(valor);
    setNomeUsuario(nomeSalvo);
    setMatriculaSAP(matriculaSAPSalva);

    (async () => {
      const resultado = await validarMatriculaSAP(valor);
      if (resultado === null) {
        localStorage.removeItem(STORAGE_MATRICULA);
        setMatricula("");
        setNomeUsuario("");
        setMatriculaSAP("");
        toast.error("Matrícula não encontrada no SAP");
      } else {
        setNomeUsuario(resultado.nome);
        setMatriculaSAP(resultado.matriculaSAP);
        localStorage.setItem(
          STORAGE_MATRICULA,
          JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
        );
      }
    })();
  }, [show, validarMatriculaSAP]);

  useEffect(() => {
    if (!/^\d{6}$/.test(matricula) || matriculaSAP) return;
    const timeout = setTimeout(async () => {
      setValidandoMatricula(true);
      const resultado = await validarMatriculaSAP(matricula);
      setValidandoMatricula(false);
      if (resultado === null) {
        setNomeUsuario("");
        setMatriculaSAP("");
        toast.error("Matrícula não encontrada no SAP");
        return;
      }
      setNomeUsuario(resultado.nome);
      setMatriculaSAP(resultado.matriculaSAP);
      localStorage.setItem(
        STORAGE_MATRICULA,
        JSON.stringify({ principal: matricula, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
      );
      toast.success(`Matrícula registrada: ${resultado.nome}`);
    }, 600);
    return () => clearTimeout(timeout);
  }, [matricula, matriculaSAP, validarMatriculaSAP]);

  useEffect(() => {
    if (!show || ordemInput.trim().length < 8 || apontandoRef.current) return;
    const ordem = ordemInput.trim();
    const timeout = setTimeout(async () => {
      const dados = await buscarPeca(ordem);
      if (dados) dispararApontamento(ordem, dados);
    }, 800);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordemInput, show]);

  const campos = dadosPeca
    ? [
        ["Ordem", dadosPeca.ordem_Serial],
        ["Descrição do Veículo", dadosPeca.ordem_VehDesc],
        ["Sub Produto", dadosPeca.subProduto_Desc],
        ["Set Atual", dadosPeca.ordem_SetAtual],
      ]
    : [];

  return (
    <Modal show={show} onHide={handleCancel} centered backdrop="static" size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold fs-3">Apontar Serigrafia</Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-4">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Matrícula SAP</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              maxLength={6}
              size="lg"
              value={matricula}
              onChange={(e) => handleMatriculaChange(e.target.value)}
              placeholder="Digite a matrícula SAP (6 dígitos)"
              isInvalid={matricula.length === 6 && !validandoMatricula && !matriculaSAP}
              isValid={!!matriculaSAP}
            />
            <Form.Control.Feedback type="invalid">Matrícula não encontrada no SAP.</Form.Control.Feedback>
            {validandoMatricula ? (
              <small className="text-muted d-block mt-1">Validando matrícula...</small>
            ) : nomeUsuario ? (
              <small className="text-success fw-semibold d-block mt-1">Operador: {nomeUsuario}</small>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Ordem</Form.Label>
            <Form.Control
              type="text"
              size="lg"
              ref={inputRef}
              autoFocus
              value={ordemInput}
              onChange={(e) => setOrdemInput(e.target.value)}
              onKeyDown={handleOrdemKeyDown}
              placeholder="Bipe ou digite a ordem"
            />
          </Form.Group>
        </Form>

        {dadosPeca && (
          <div className="border rounded p-3 bg-light">
            <div className="text-center mb-3">
              <span className="text-muted d-block fs-5">LOTE</span>
              <span className="fw-bold text-primary" style={{ fontSize: "2.6rem", lineHeight: 1 }}>
                {dadosPeca.ordem_Nro_Lote || "—"}
              </span>
            </div>
            <ul className="list-unstyled mb-0">
              {campos.map(([chave, valor]) => (
                <li key={chave} className="d-flex justify-content-between border-top py-2 fs-5">
                  <span className="text-muted">{chave}</span>
                  <span className="fw-semibold text-end">{String(valor ?? "")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <button className="btn btn-outline-secondary btn-lg px-4" onClick={handleCancel}>
          Cancelar
        </button>
        <button
          className="btn btn-success btn-lg px-5 fw-bold"
          onClick={() => dispararApontamento(ordemInput)}
          disabled={!matriculaSAP || validandoMatricula || !ordemInput.trim() || isSubmitting}
        >
          {isSubmitting ? "Apontando..." : "✓ Apontar"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalApontamentoSerigrafia;
